import { emitHintSignal } from '@flobi/asset-library/hints';
import { useRef, type ReactNode } from 'react';
import { Menu, useMenu, type MenuEntry } from '../../ui/menu';
import { tip } from '../../ui/tooltip';
import { Icon, ICON_SIZE, type IconName } from '../chrome/icons';
import type { ToolId } from '../useEditorCanvas';
import type { Rail, RailButton } from '../tools/types';
import { KINDS } from '../tools/shapes';
import { ALL_TOOLS } from './Toolbar';
import { type SelectionActionsBarProps } from './SelectionActionsBar';
import { useProTools } from '../pro';
import { ADDONS_HIDDEN } from '../../lib/hiddenFeatures';

/**
 * The dock, Figma's bottom bar in Artwork's marks (Tarek, 1 Nov): tools as marks named in their
 * tooltips, each with an arrow beside it. A press on the mark takes the tool up at once; the
 * arrow opens what else lives there, and a family wears the one last used. With the Pro tools
 * on, Pen, Reshape, Combine, Repeat and Artboard join the four everyday tools.
 *
 * Past a hairline stand the two modes, Edit and Animate. In Animate the tools step aside and the
 * modes stay, so the way back is where it was. The selection's own bar and a tool's options
 * live in the context bar over the canvas, not here.
 */

/** What the dock is handed; the phone's tool tray (`PhoneToolTray`) takes the same. */
export interface CreateEditDockProps extends SelectionActionsBarProps {
  tool: ToolId;
  textModes: readonly RailButton[];
  selectionModes: readonly RailButton[];
  hiddenTools: ReadonlySet<string>;
  assetsOpen: boolean;
  onAssets: () => void;
  onAddons: () => void;
  addonsOpen: boolean;
  addonName?: string | null;
  /** Drawing tools and point editing keep their own contextual options. */
  options?: ReactNode;
  editingPoints?: boolean;
  /** The Comment tool (C, W5-AR2): on while pins are being dropped. */
  commenting?: boolean;
  onComment?: () => void;
  /** A tool's own modes, read when its arrow is pressed. */
  railOf?: (tool: ToolId) => Rail | null;
  /** The hand: Select's arrow holds it, beside the zoom pill's. */
  panning?: boolean;
  onPan?: () => void;
  /** The two modes. Absent, there are none: a file with Animate turned off. */
  animating?: boolean;
  onMode?: () => void;
}

/** The families that stand in the dock with the Pro tools on, after the everyday four; each opens on its first. */
/**
 * The dock wears Streamline Core Line marks (Tarek's pick of the five layouts, 2 Nov). A tool
 * without one here keeps its own mark; the menus keep theirs.
 */
const DOCK_MARK: Partial<Record<ToolId, IconName>> = {
  select: 'dock-select', rect: 'dock-rect', ellipse: 'dock-ellipse', polygon: 'dock-polygon', star: 'dock-star',
  text: 'dock-text', draw: 'dock-draw', pen: 'dock-pen', liquify: 'dock-brush', builder: 'dock-combine', cut: 'dock-cut',
  cloner: 'dock-repeat', artboard: 'dock-artboard', measure: 'dock-measure',
};
const dockMark = (id: ToolId, own: IconName): IconName => DOCK_MARK[id] ?? own;

const PRO_FAMILIES: readonly (readonly ToolId[])[] = [['liquify', 'warp'], ['builder', 'cut'], ['cloner', 'blend'], ['artboard', 'measure', 'slice']];

export function CreateEditDock(props: CreateEditDockProps) {
  const { tool, onTool, hiddenTools, railOf, animating = false, onMode } = props;
  const pro = useProTools();
  const more = useMenu();
  const activeTool: ToolId = tool === 'width' ? 'liquify' : tool;
  const choose = (next: ToolId) => onTool(next);
  const mark = (id: ToolId) => ALL_TOOLS.find((one) => one.id === id);
  /** What each family last had in hand: its mark, and what a press takes up. */
  const worn = useRef(new Map<ToolId, ToolId>());
  const wornOf = (ids: readonly ToolId[]) => {
    if (ids.includes(activeTool)) worn.current.set(ids[0]!, activeTool);
    return worn.current.get(ids[0]!) ?? ids[0]!;
  };
  const shapeIds = KINDS.map(kind => kind.id as ToolId);
  const shapeInHand = wornOf(shapeIds);
  const shapes = (): MenuEntry[] => KINDS.map(kind => ({ id: kind.id, label: kind.title, icon: kind.icon, guide: kind.id, checked: tool === kind.id, onSelect: () => { if (kind.id === 'rect' || kind.id === 'ellipse') emitHintSignal('tool.fromMenu', { keyed: true }); choose(kind.id); } }));
  const textItems = (): MenuEntry[] => props.textModes.map(mode => ({
    id: mode.id, label: mode.title.split(' — ')[0]!, icon: mode.icon, checked: mode.pressed, guide: textGuide(mode.id),
    onSelect: () => { mode.run({ more: false }); choose('text'); },
  }));
  const entry = (id: ToolId): MenuEntry[] => {
    const one = mark(id);
    return one && !hiddenTools.has(id) ? [{ id, label: one.label, icon: one.icon, shortcut: one.shortcut, guide: id, checked: activeTool === id, onSelect: () => { if (one.shortcut) emitHintSignal('tool.fromMenu', { keyed: true }); choose(id); } }] : [];
  };
  const entries = (ids: readonly ToolId[]): MenuEntry[] => ids.flatMap(entry);
  /** A tool's modes, as its strip has them, a hairline between its groups. */
  const modesOf = (id: ToolId): MenuEntry[] => {
    const well = railOf?.(id)?.top;
    if (well?.kind !== 'buttons') return [];
    return well.items.flatMap((mode, at): MenuEntry[] => [
      ...(at > 0 && well.items[at - 1]!.panelGroup !== mode.panelGroup ? [{ kind: 'separator' as const }] : []),
      // Width is a tool of its own, which its button takes up itself.
      { id: mode.id, label: mode.title.split(' — ')[0]!, icon: mode.icon, checked: activeTool === id && mode.pressed, onSelect: () => { mode.run({ more: false }); if (mode.id !== 'width') choose(id); } },
    ]);
  };
  const selectItems = (): MenuEntry[] => [
    ...entry('select'),
    ...(props.onPan ? [{ id: 'hand', label: 'Hand', icon: 'hand' as const, shortcut: 'Space', checked: !!props.panning, onSelect: props.onPan }] : []),
  ];
  // More is what has no family: the short list in Simple, Add-ons in Pro.
  const moreItems: MenuEntry[] = pro
    ? (ADDONS_HIDDEN ? [] : [{ id: 'addons', label: 'Add-ons…', icon: 'addons' as const, checked: props.addonsOpen || tool === 'addon', onSelect: props.onAddons }])
    : [
        ...entries(['pen', 'artboard', 'measure']),
      ];
  const proFamilies = pro ? PRO_FAMILIES.map(ids => ids.filter(id => !hiddenTools.has(id))).filter(ids => ids.length > 0) : [];
  // A specialist tool in hand that has no button of its own reads on More.
  const inDock = new Set<ToolId>(['select', 'draw', 'text', ...shapeIds, ...(pro ? ['pen' as const, ...proFamilies.flat()] : [])]);
  const heldElsewhere = tool !== 'select' && !inDock.has(activeTool);
  const held = heldElsewhere ? (tool === 'addon' ? { label: props.addonName ?? 'Add-on', icon: 'addons' as IconName } : mark(activeTool)) : null;
  const modes = onMode && !hiddenTools.has('animate');

  return <div className="create-edit-dock dock-float pointer-events-auto" role="toolbar" aria-label="Tools" data-tour="artwork.dock" data-learn-avoid="">
    {animating && modes ? null : <div className="dock-tools">
      <DockTool label="Select" icon={dockMark('select', 'cursor')} shortcut="V" pressed={tool === 'select'} onClick={() => choose('select')} guide="select" items={selectItems} />
      <DockTool label="Shape" icon={dockMark(shapeInHand, KINDS.find(kind => kind.id === shapeInHand)!.icon)} shortcut="M" pressed={shapeIds.includes(tool)} onClick={() => choose(shapeInHand)} items={shapes} />
      {!hiddenTools.has('text') && <DockTool label="Text" icon={dockMark('text', 'tools-text')} shortcut="T" pressed={tool === 'text'} onClick={() => choose('text')} guide="text" items={textItems} />}
      <DockTool label="Draw" icon={dockMark('draw', 'tools-draw')} shortcut="D" pressed={tool === 'draw'} onClick={() => choose('draw')} guide="draw" items={() => modesOf('draw')} />
      {pro && !hiddenTools.has('pen') ? <DockTool label="Pen" icon={dockMark('pen', mark('pen')!.icon)} shortcut={mark('pen')!.shortcut} pressed={activeTool === 'pen'} onClick={() => choose('pen')} guide="pen" items={() => modesOf('pen')} /> : null}
      {proFamilies.map(ids => {
        const one = mark(wornOf(ids))!;
        return <DockTool key={ids[0]} label={one.label} icon={dockMark(one.id as ToolId, one.icon)} shortcut={one.shortcut} pressed={ids.includes(activeTool)} onClick={() => choose(one.id as ToolId)} guide={one.id} items={() => [...(ids[0] === 'liquify' ? modesOf('liquify') : []), ...(ids[0] === 'liquify' ? [{ kind: 'separator' as const }] : []), ...entries(ids[0] === 'liquify' ? ids.slice(1) : ids)]} />;
      })}
      {props.onComment ? <button type="button" className="dock-tool" aria-label="Comment" aria-pressed={!!props.commenting} data-tour="artwork.comment" {...tip('Comment', 'C')} onClick={props.onComment}><Icon name="dock-comment" size={20} /></button> : null}
      {moreItems.length > 0 || held ? <>
        <button type="button" ref={more.ref} className="dock-tool" aria-label={held ? `${held.label}, in More` : 'More tools'} aria-haspopup="menu" aria-expanded={more.open} aria-pressed={heldElsewhere || undefined} {...tip(held ? held.label : 'More tools')} onClick={more.toggle}>
          <Icon name={held ? held.icon : 'more'} size={20} />
        </button>
        <Menu open={more.open} anchor={more.anchor} onClose={more.close} placement="above" align="end" label="More tools" width={240} items={moreItems} />
      </> : null}
    </div>}
    {modes ? <>
      {animating ? null : <span className="dock-divider" aria-hidden />}
      <div className="dock-mode-well" role="group" aria-label="Mode">
        <button type="button" className="dock-mode" aria-label="Edit" aria-pressed={!animating} {...tip('Edit')} onClick={() => { if (animating) onMode(); }}><Icon name="dock-edit" size={18} /></button>
        <button type="button" className="dock-mode" aria-label="Animate" aria-pressed={animating} data-tour="artwork.animate" {...tip('Animate')} onClick={() => { if (!animating) onMode(); }}><Icon name="dock-animate" size={18} /></button>
      </div>
    </> : null}
  </div>;
}

/** A tool: its mark takes it up, the arrow beside it opens what else lives there. */
function DockTool({ label, icon, shortcut, pressed, onClick, guide, items }: { label: string; icon: IconName; shortcut?: string; pressed: boolean; onClick: () => void; guide?: string; items: () => readonly MenuEntry[] }) {
  const menu = useMenu();
  return <span className="dock-pair">
    <button type="button" className="dock-tool" aria-label={label} aria-pressed={pressed} data-guide={guide} data-tour={`artwork.tool.${label.toLowerCase()}`} {...tip(label, shortcut)} onClick={onClick}>
      <Icon name={icon} size={20} />
    </button>
    <button type="button" ref={menu.ref} className="dock-arrow" aria-label={`${label} options`} aria-haspopup="menu" aria-expanded={menu.open} {...tip(`${label} options`)} onClick={menu.toggle}>
      <Icon name="chevron" size={ICON_SIZE.indicator} />
    </button>
    {menu.open ? <Menu open anchor={menu.anchor} onClose={menu.close} placement="above" align="center" label={label} items={items()} /> : null}
  </span>;
}

/** The guide key of a type mode: Point type is the Text tool's own. */
function textGuide(mode: string): string {
  return mode === 'point' ? 'text' : `text:${mode}`;
}
